import { useEffect, useRef, useCallback } from 'react';
import { isNearBottom, scrollContainerToBottom } from '../utils/chatScroll';

/**
 * Keeps chat scrolled to bottom only when appropriate:
 * - first open / initial messages load
 * - new messages while user is already near the bottom
 * - explicit scrollToBottom() after sending (force)
 */
export function useChatAutoScroll({
  containerRef,
  conversationKey,
  messageCount = 0,
  enabled = true,
}) {
  const stickToBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const lastConversationKeyRef = useRef(conversationKey);

  useEffect(() => {
    if (lastConversationKeyRef.current !== conversationKey) {
      lastConversationKeyRef.current = conversationKey;
      stickToBottomRef.current = true;
      prevMessageCountRef.current = 0;
    }
  }, [conversationKey]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return undefined;

    const onScroll = () => {
      stickToBottomRef.current = isNearBottom(el);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [containerRef, conversationKey, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;

    const prev = prevMessageCountRef.current;
    const count = messageCount;
    const isInitialLoad = prev === 0 && count > 0;
    const hasNewMessages = count > prev;

    if (isInitialLoad || (hasNewMessages && stickToBottomRef.current)) {
      requestAnimationFrame(() => {
        scrollContainerToBottom(
          el,
          hasNewMessages && !isInitialLoad ? 'smooth' : 'auto'
        );
      });
    }

    prevMessageCountRef.current = count;
  }, [messageCount, conversationKey, containerRef, enabled]);

  const scrollToBottom = useCallback(
    (behavior = 'smooth') => {
      const el = containerRef.current;
      if (!el) return;
      stickToBottomRef.current = true;
      scrollContainerToBottom(el, behavior);
    },
    [containerRef]
  );

  return { scrollToBottom };
}

export default useChatAutoScroll;
